import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Phone, Plus, Pencil, Trash2, Mail, Search } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";

const CATEGORIE = ["Emergenze", "Sanità", "Fornitori", "Manutenzione", "Uffici", "Trasporti", "Famiglie", "Altro"];

export default function NumeriUtili() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null);
  const { data: list = [] } = useQuery({ queryKey: ["numeri_utili"], queryFn: async () => (await api.get("/numeri_utili")).data });

  const save = async () => {
    if (!dlg.nome || !dlg.telefono) { toast.error("Nome e telefono obbligatori"); return; }
    try {
      if (dlg.id) await api.put(`/numeri_utili/${dlg.id}`, dlg);
      else await api.post("/numeri_utili", dlg);
      toast.success("Salvato"); setDlg(null); qc.invalidateQueries({ queryKey: ["numeri_utili"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const remove = async (id) => { await api.delete(`/numeri_utili/${id}`); toast.success("Eliminato"); qc.invalidateQueries({ queryKey: ["numeri_utili"] }); };

  const filtered = list.filter((n) => `${n.nome} ${n.categoria} ${n.telefono} ${n.note || ""}`.toLowerCase().includes(q.toLowerCase()));
  const groups = filtered.reduce((a, n) => { const k = n.categoria || "Altro"; (a[k] = a[k] || []).push(n); return a; }, {});

  return (
    <div>
      <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><Phone size={22} /> Numeri utili</h1>
          <p className="text-sm text-muted-foreground">Rubrica telefonica condivisa · {list.length} contatti</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative"><Search size={15} className="absolute left-2.5 top-2.5 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca..." className="pl-8 w-52" data-testid="numeri-search" /></div>
          <Button onClick={() => setDlg({ categoria: "Fornitori" })} data-testid="numeri-new"><Plus size={16} className="mr-1" /> Nuovo numero</Button>
        </div>
      </div>

      {Object.keys(groups).length === 0 && <div className="gr-card p-10 text-center text-muted-foreground">Nessun contatto. Aggiungi il primo numero utile.</div>}
      <div className="space-y-6">
        {Object.entries(groups).map(([cat, items]) => (
          <div key={cat}>
            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">{cat}</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((n) => (
                <div key={n.id} className="gr-card p-4 flex items-start gap-3" data-testid={`numero-${n.id}`}>
                  <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0"><Phone size={18} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-head font-bold leading-tight truncate">{n.nome}</div>
                    <a href={`tel:${n.telefono}`} className="text-sm font-semibold text-primary block">{n.telefono}</a>
                    {n.email && <div className="text-xs text-muted-foreground flex items-center gap-1 truncate"><Mail size={11} /> {n.email}</div>}
                    {n.note && <div className="text-xs text-muted-foreground truncate">{n.note}</div>}
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => setDlg(n)} data-testid={`numero-edit-${n.id}`} className="text-muted-foreground hover:text-primary"><Pencil size={15} /></button>
                    <button onClick={() => remove(n.id)} data-testid={`numero-del-${n.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{dlg?.id ? "Modifica numero" : "Nuovo numero utile"}</DialogTitle><DialogDescription className="text-xs">Contatto della rubrica condivisa</DialogDescription></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div><Label className="text-xs font-semibold mb-1 block">Nome / Ente *</Label><Input value={dlg.nome || ""} onChange={(e) => setDlg((p) => ({ ...p, nome: e.target.value }))} data-testid="numero-nome" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Categoria</Label>
                <select value={dlg.categoria || "Altro"} onChange={(e) => setDlg((p) => ({ ...p, categoria: e.target.value }))} className="w-full h-10 rounded-md border px-3 text-sm" data-testid="numero-categoria">
                  {CATEGORIE.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold mb-1 block">Telefono *</Label><Input value={dlg.telefono || ""} onChange={(e) => setDlg((p) => ({ ...p, telefono: e.target.value }))} data-testid="numero-telefono" /></div>
                <div><Label className="text-xs font-semibold mb-1 block">Email</Label><Input value={dlg.email || ""} onChange={(e) => setDlg((p) => ({ ...p, email: e.target.value }))} data-testid="numero-email" /></div>
              </div>
              <div><Label className="text-xs font-semibold mb-1 block">Note</Label><Input value={dlg.note || ""} onChange={(e) => setDlg((p) => ({ ...p, note: e.target.value }))} data-testid="numero-note" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button><Button onClick={save} data-testid="numero-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
